const ORG_ALIAS_TOKEN_PATTERN = /[A-Za-z0-9]+/g;
const SINGLE_WORD_ORG_ALIAS_LENGTH = 3;
const RESERVED_ORG_ALIASES = new Set(['kai']);

export const ORG_ALIAS_MAX_LENGTH = 4;

const getOrgAliasTokens = (orgName: string) => orgName.match(ORG_ALIAS_TOKEN_PATTERN) ?? [];

const getSignificantOrgAliasTokens = (tokens: string[]) => tokens.filter(token => !/^\d+$/.test(token));

/**
 * Normalize a manually configured org alias.
 */
export const normalizeOrgAlias = (orgAlias?: string | null) => {
  const trimmedOrgAlias = orgAlias?.trim();
  return trimmedOrgAlias
    ? trimmedOrgAlias.slice(0, ORG_ALIAS_MAX_LENGTH)
    : undefined;
};

/**
 * Whether the alias is reserved and cannot be configured manually.
 */
export const isReservedOrgAlias = (orgAlias?: string | null) => {
  const normalizedOrgAlias = normalizeOrgAlias(orgAlias);
  return (
    !!normalizedOrgAlias
    && RESERVED_ORG_ALIASES.has(normalizedOrgAlias.toLowerCase())
  );
};

/**
 * Derive a compact org alias from the org name when no manual alias is configured.
 */
export const getOrgAliasFromName = (orgName?: string | null) => {
  const normalizedOrgName = orgName?.trim();
  if (!normalizedOrgName) return undefined;

  const significantTokens = getSignificantOrgAliasTokens(
    getOrgAliasTokens(normalizedOrgName),
  );

  if (significantTokens.length === 0) return undefined;

  if (significantTokens.length === 1) {
    return significantTokens[0]
      .slice(0, SINGLE_WORD_ORG_ALIAS_LENGTH)
      .toUpperCase();
  }

  return significantTokens
    .map(token => token[0]?.toUpperCase() ?? '')
    .join('')
    .slice(0, ORG_ALIAS_MAX_LENGTH);
};

/**
 * Build the property ID field label for the current organization.
 */
export const getPropertyIdLabel = (
  orgName?: string | null,
  orgAlias?: string | null,
) => {
  const resolvedOrgAlias = normalizeOrgAlias(orgAlias) ?? getOrgAliasFromName(orgName);

  return resolvedOrgAlias ? `${resolvedOrgAlias} ID` : 'Property ID';
};
